/**
 * Herkomst-melding voor producten die buiten de EU worden gemaakt en verzonden
 * (besluit Gert 13 okt 2026: geen IOSS-registratie, dus de klant moet vooraf
 * weten dat bij levering invoerkosten kunnen komen).
 *
 * Bron van waarheid is `src/data/uk-origin-products.json` (met bewijs; runtime-kopie zonder leveranciersgegevens: `shipping-origin-runtime.json`): een handmatig
 * gecontroleerde lijst per product (en per SKU), onderbouwd met de read-only
 * leveranciers-API (zie `scripts/audit-uk-origin-products.ts`). Daarnaast blijft
 * het oudere veld `specifications.shippingOrigin` werken.
 *
 * Client-safe: geen prisma, alleen een kleine JSON. Winkelwagens die al in
 * localStorage staan hebben geen herkomstveld; de cart leidt het daarom af uit
 * productId/slug en de gekozen SKU.
 *
 * Geen leveranciersnamen in klanttekst en geen gedachtestreepjes.
 */
import decisions from '@/data/shipping-origin-runtime.json';

export type ShippingOriginCountry = 'GB' | 'US';
type NoticeLocale = 'nl' | 'en' | 'de' | 'fr';

type OriginDecision = {
  productId: string;
  slug: string;
  shipsFromCountry: ShippingOriginCountry;
  /** SKU's die vanuit dit land komen. Leeg of ontbrekend = alle uitvoeringen. */
  skus?: string[];
  /** Alle SKU's van het product (om "alle uitvoeringen" te kunnen vaststellen). */
  allSkus?: string[];
};

// Alleen het VK: sinds 14 okt 2026 leveren we niets meer vanuit de VS (die
// uitvoeringen zijn niet te kiezen, zie supplier-delivery-restrictions.ts),
// dus ook een oud `specifications.shippingOrigin: 'US'` geeft geen melding.
const SUPPORTED_ORIGINS = new Set<ShippingOriginCountry>(['GB']);
const ENTRIES = (decisions as { products: OriginDecision[] }).products.filter((entry) =>
  SUPPORTED_ORIGINS.has(entry.shipsFromCountry),
);
const BY_ID = new Map(ENTRIES.map((entry) => [entry.productId, entry]));
const BY_SLUG = new Map(ENTRIES.map((entry) => [entry.slug, entry]));

function normalizeSku(value: unknown): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  return raw.replace(/^rotate:/, '').toUpperCase();
}

/** De gekozen leveranciers-SKU uit een opties-object van configurator of cart. */
export function selectedSkuFromOptions(options: Record<string, unknown> | null | undefined): string | null {
  if (!options) return null;
  return normalizeSku(options.variantSku ?? options.prodigiSku ?? options.sku);
}

function legacyOrigin(specifications: unknown): ShippingOriginCountry | null {
  const origin = String((specifications as Record<string, unknown> | null)?.shippingOrigin || '').toUpperCase();
  return SUPPORTED_ORIGINS.has(origin as ShippingOriginCountry) ? (origin as ShippingOriginCountry) : null;
}

/**
 * Herkomstland van een product (of van een gekozen uitvoering), of null als het
 * product binnen de EU wordt gemaakt.
 *
 * - Met `sku`: alleen een melding als die uitvoering in de lijst staat.
 * - Zonder `sku`: alleen een melding als elke uitvoering van buiten de EU komt,
 *   zodat we nooit "gemaakt in het VK" zeggen over een EU-uitvoering.
 */
export function resolveShippingOrigin(input: {
  productId?: string | null;
  productSlug?: string | null;
  sku?: string | null;
  specifications?: unknown;
}): ShippingOriginCountry | null {
  const entry = (input.productId && BY_ID.get(input.productId)) || (input.productSlug && BY_SLUG.get(input.productSlug)) || null;
  if (entry) {
    const skus = (entry.skus || []).map((sku) => sku.toUpperCase());
    if (!skus.length) return entry.shipsFromCountry;
    const sku = normalizeSku(input.sku);
    if (sku) return skus.includes(sku) ? entry.shipsFromCountry : null;
    const all = (entry.allSkus || []).map((value) => value.toUpperCase());
    return all.length > 0 && all.every((value) => skus.includes(value)) ? entry.shipsFromCountry : null;
  }
  return legacyOrigin(input.specifications);
}


const COUNTRY: Record<ShippingOriginCountry, Record<NoticeLocale, string>> = {
  GB: { nl: 'het Verenigd Koninkrijk', en: 'the United Kingdom', de: 'dem Vereinigten Königreich', fr: 'le Royaume-Uni' },
  US: { nl: 'de Verenigde Staten', en: 'the United States', de: 'den Vereinigten Staaten', fr: 'les États-Unis' },
};

/** "im Vereinigten Königreich" / "in den Vereinigten Staaten" voor de Duitse zin. */
const DE_IN: Record<ShippingOriginCountry, string> = {
  GB: 'im Vereinigten Königreich',
  US: 'in den Vereinigten Staaten',
};

export function noticeLocale(locale: string | null | undefined): NoticeLocale {
  return (['nl', 'en', 'de', 'fr'] as const).includes(locale as NoticeLocale) ? (locale as NoticeLocale) : 'en';
}

export function shippingOriginNoticeCopy(country: ShippingOriginCountry, locale: string | null | undefined) {
  const lang = noticeLocale(locale);
  const land = COUNTRY[country][lang];
  switch (lang) {
    case 'nl':
      return {
        title: `Verzonden vanuit ${land}`,
        body: `Dit product wordt gemaakt en verzonden vanuit ${land}. Bij levering kunnen invoerbtw, invoerrechten en afhandelingskosten van de vervoerder in rekening worden gebracht.`,
      };
    case 'de':
      return {
        title: `Versand aus ${land}`,
        body: `Dieses Produkt wird ${DE_IN[country]} hergestellt und von dort versendet. Bei der Zustellung können Einfuhrumsatzsteuer, Einfuhrzölle und Bearbeitungsgebühren des Zustellers anfallen.`,
      };
    case 'fr':
      return {
        title: `Expédié depuis ${land}`,
        body: `Ce produit est fabriqué et expédié depuis ${land}. À la livraison, la TVA à l’importation, des droits de douane et des frais de traitement du transporteur peuvent être facturés.`,
      };
    default:
      return {
        title: `Ships from ${land}`,
        body: `This product is made in and shipped from ${land}. Import VAT, import duties and carrier handling fees may be charged on delivery.`,
      };
  }
}

/** Standaard-SKU uit de productopties (het `variantSku`-optieveld), voor de eerste render. */
export function defaultSkuFromProductOptions(options: unknown): string | null {
  if (!Array.isArray(options)) return null;
  const option = options.find((entry) => ['variantSku', 'prodigiSku'].includes(String((entry as { id?: unknown })?.id)));
  if (!option) return null;
  const typed = option as { default?: unknown; values?: Array<{ value?: unknown; available?: boolean }> };
  return normalizeSku(typed.default ?? typed.values?.find((value) => value.available !== false)?.value);
}
